import { useMemo } from 'react';
import { ConditionalRule } from '../types/conditional-logic.types';
import { FormField } from '../types/form-builder.types';
import { evaluateCondition } from '../utils/conditionalLogicUtils';

interface ConditionalLogicResult {
  visibleFields: Set<string>;
  hiddenFields: Set<string>;
  enabledFields: Set<string>;
  disabledFields: Set<string>;
  requiredFields: Set<string>;
  optionalFields: Set<string>;
}

/**
 * Hook to evaluate and apply conditional logic rules to form fields
 */
export function useConditionalLogic(
  rules: ConditionalRule[],
  formData: Record<string, any>,
  allFields: FormField[]
): ConditionalLogicResult {
  return useMemo(() => {
    const result: ConditionalLogicResult = {
      visibleFields: new Set(),
      hiddenFields: new Set(),
      enabledFields: new Set(),
      disabledFields: new Set(),
      requiredFields: new Set(),
      optionalFields: new Set(),
    };

    // If no rules, all fields visible and enabled by default
    if (!rules || rules.length === 0) {
      allFields.forEach(field => {
        result.visibleFields.add(field.id);
        result.enabledFields.add(field.id);
        if (field.config.required) {
          result.requiredFields.add(field.id);
        }
      });
      return result;
    }

    // Create mapping from fieldName to fieldId
    const fieldNameToId = new Map<string, string>();
    const fieldIdToName = new Map<string, string>();

    allFields.forEach(field => {
      const fieldName = field.config.fieldName || field.id;
      fieldNameToId.set(fieldName, field.id);
      fieldIdToName.set(field.id, fieldName);
    });

    // Collect all fields that are targets of show_field/hide_field actions
    // Map field names to field IDs
    const showFieldTargets = new Set<string>();
    const hideFieldTargets = new Set<string>();

    rules.forEach(rule => {
      if (!rule.enabled) return;

      rule.actions.forEach(action => {
        const targetFieldNames = action.targetFieldNames || [];
        if (action.type === 'show_field') {
          targetFieldNames.forEach(fieldName => {
            const fieldId = fieldNameToId.get(fieldName);
            if (fieldId) showFieldTargets.add(fieldId);
          });
        } else if (action.type === 'hide_field') {
          targetFieldNames.forEach(fieldName => {
            const fieldId = fieldNameToId.get(fieldName);
            if (fieldId) hideFieldTargets.add(fieldId);
          });
        }
      });
    });

    // Initialize fields visibility
    allFields.forEach(field => {
      // If field is a target of show_field action, hide it by default
      if (showFieldTargets.has(field.id)) {
        result.hiddenFields.add(field.id);
      }
      // If field is a target of hide_field action, show it by default
      else if (hideFieldTargets.has(field.id)) {
        result.visibleFields.add(field.id);
      }
      // Otherwise, show by default
      else {
        result.visibleFields.add(field.id);
      }

      // All fields enabled by default
      result.enabledFields.add(field.id);

      // Set required status
      if (field.config.required) {
        result.requiredFields.add(field.id);
      }
    });

    // Create remapped formData using field names as keys (for condition evaluation)
    const remappedFormData: Record<string, any> = {};
    Object.keys(formData).forEach(fieldId => {
      const fieldName = fieldIdToName.get(fieldId) || fieldId;
      remappedFormData[fieldName] = formData[fieldId];
    });

    // Process each enabled rule
    rules.forEach(rule => {
      if (!rule.enabled) return;

      // Evaluate all conditions for this rule using remapped form data
      const conditionResults = rule.conditions.map(condition =>
        evaluateCondition(condition, remappedFormData)
      );

      // Check if rule should trigger based on logic operator
      let shouldTrigger = false;
      if (rule.logicOperator === 'AND') {
        shouldTrigger = conditionResults.every(result => result === true);
      } else {
        shouldTrigger = conditionResults.some(result => result === true);
      }

      // If rule triggers, apply all actions
      if (shouldTrigger) {
        rule.actions.forEach(action => {
          const targetFieldNames = action.targetFieldNames || [];

          // Convert field names to field IDs
          const targetFieldIds = targetFieldNames
            .map(name => fieldNameToId.get(name))
            .filter(Boolean) as string[];

          switch (action.type) {
            case 'show_field':
              targetFieldIds.forEach(fieldId => {
                result.visibleFields.add(fieldId);
                result.hiddenFields.delete(fieldId);
              });
              break;

            case 'hide_field':
              targetFieldIds.forEach(fieldId => {
                result.hiddenFields.add(fieldId);
                result.visibleFields.delete(fieldId);
              });
              break;

            case 'enable_field':
              targetFieldIds.forEach(fieldId => {
                result.enabledFields.add(fieldId);
                result.disabledFields.delete(fieldId);
              });
              break;

            case 'disable_field':
              targetFieldIds.forEach(fieldId => {
                result.disabledFields.add(fieldId);
                result.enabledFields.delete(fieldId);
              });
              break;

            case 'require_field':
              targetFieldIds.forEach(fieldId => {
                result.requiredFields.add(fieldId);
                result.optionalFields.delete(fieldId);
              });
              break;

            case 'make_optional':
              targetFieldIds.forEach(fieldId => {
                result.optionalFields.add(fieldId);
                result.requiredFields.delete(fieldId);
              });
              break;

            case 'set_value':
              // This would be handled separately in the form submission context
              break;

            case 'clear_value':
              // This would be handled separately in the form submission context
              break;

            // Other actions can be added as needed
            default:
              break;
          }
        });
      }
    });

    return result;
  }, [rules, formData, allFields]);
}

